import * as React from 'react'
import { observer } from 'mobx-react'
import { QuestionEditorComponentProps } from 'jobs/questions/props'
import TextQuestionViewModel from '../../TextQuestionViewModel'
import TextArea from 'components/inputs/TextArea'
import { useDraftState } from 'hooks/state-hooks'
import { Help } from 'components/inputs'
import { last } from 'lodash'

interface Props extends QuestionEditorComponentProps<TextQuestionViewModel> {}

export default observer(function TextEditorInput({
  vm,
  disabled,
  onEnterDown,
  focus$,
  onFocus,
  isNotValid,
}: Props) {
  const [value, setValue, commit] = useDraftState(vm.answer || '', vm.change)

  const helpText = React.useMemo(() => {
    if (!vm.validations?.maxLength || !value.length) {
      return undefined
    }

    return `${vm.validations.maxLength - value.length} characters remaining`
  }, [value, vm.validations?.maxLength])

  const keyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !vm.settings?.multiLine) {
        e.preventDefault()
        commit()
        onEnterDown?.(e)
      }

      if (vm.settings?.multiLine) {
        setValue((e.target as HTMLTextAreaElement).value)
        commit()
      }
    },
    [onEnterDown, commit]
  )

  return (
    <div>
      <TextArea
        autoSize
        disabled={disabled}
        rows={1}
        focus$={focus$}
        onFocus={onFocus}
        value={value}
        onBlur={commit}
        placeholder={
          disabled || vm.settings?.multiLine
            ? 'Text'
            : 'Text (Shift + Enter for new line)'
        }
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={keyDown}
        isNotValid={isNotValid}
        data-test={`text-answer-${vm.id}`}
      />

      <Help
        error={vm.settings?.showErrorMessages ? last(vm.validationErrors) : ''}
        help={helpText}
      />
    </div>
  )
})
